import { useSyncExternalStore } from "react";

/**
 * Every keyboard shortcut in ZeaOps, in one place: the shortcuts overlay ("?"), the Help screen
 * and the global key handler all read from here, so what is listed is what works.
 *
 * Single-character shortcuts ("?", "/", "T", "G then H") can be turned off, as WCAG 2.1.4 asks,
 * for people who use speech input or press keys by accident. Ctrl K always works.
 */

/** One way to press a shortcut: keys pressed together, such as ["Ctrl", "K"]. */
export type Chord = string[];

export interface Shortcut {
  /** Alternatives, such as Ctrl K or ⌘ K. */
  keys: Chord[];
  /** "G then H": the chords are pressed one after the other, not as alternatives. */
  sequence?: boolean;
  what: string;
  /** A character key on its own, which the single-key switch turns off. */
  singleKey?: boolean;
}

export interface ShortcutGroup {
  title: string;
  items: Shortcut[];
}

/** Where "G then a letter" goes. The letter is the destination's first letter where it can be. */
export const GO_KEYS: { key: string; to: string; label: string }[] = [
  { key: "h", to: "/", label: "Home" },
  { key: "n", to: "/needs-you", label: "Needs you" },
  { key: "c", to: "/cases", label: "Cases" },
  { key: "f", to: "/flows", label: "Flows" },
  { key: "t", to: "/templates", label: "Templates" },
  { key: "r", to: "/crew", label: "Crew" },
  { key: "a", to: "/ask", label: "Ask" },
  { key: "i", to: "/connections", label: "Connections" },
  { key: "o", to: "/outcomes", label: "Outcomes" },
  { key: "s", to: "/settings", label: "Settings" },
];

export const GLOBAL_SHORTCUTS: ShortcutGroup[] = [
  {
    title: "Anywhere",
    items: [
      {
        keys: [
          ["Ctrl", "K"],
          ["⌘", "K"],
        ],
        what: "Search, jump to a screen or run a command",
      },
      { keys: [["/"]], what: "Search", singleKey: true },
      { keys: [["?"]], what: "Show these shortcuts", singleKey: true },
      { keys: [["T"]], what: "Switch between light and dark", singleKey: true },
      { keys: [["Esc"]], what: "Close a dialog, a menu or a panel" },
    ],
  },
  {
    title: "Go to",
    items: GO_KEYS.map((g) => ({
      keys: [["G"], [g.key.toUpperCase()]],
      sequence: true,
      what: g.label,
      singleKey: true,
    })),
  },
  {
    title: "In search",
    items: [
      { keys: [["↑"], ["↓"]], what: "Move through the results" },
      { keys: [["Enter"]], what: "Open the result, or run the command" },
    ],
  },
];

interface ScreenShortcuts extends ShortcutGroup {
  matches: (pathname: string) => boolean;
}

const HOLD: Shortcut = {
  keys: [["Space"], ["Enter"]],
  what: "Hold on a focused approve button for 1.2 seconds to approve; letting go early cancels",
};

/** Shortcuts that only work on one screen, shown first in the overlay when you're on it. */
export const SCREEN_SHORTCUTS: ScreenShortcuts[] = [
  {
    title: "Needs you",
    matches: (p) => p === "/needs-you" || p.startsWith("/needs-you/"),
    items: [
      {
        keys: [["A"]],
        what: "Approve: a fix brings focus to the hold button; a handoff or review acts at once",
        singleKey: true,
      },
      HOLD,
      {
        keys: [["Any key"]],
        what: "After a decision, move on to the next request; arrow keys stay put",
      },
    ],
  },
  {
    title: "Case",
    matches: (p) => /^\/cases\/[^/]+$/.test(p),
    items: [
      { keys: [["←"], ["→"]], what: "Move between Root cause, Thread and Evidence" },
      { keys: [["←"], ["→"]], what: "On the replay track, step back or forward one moment" },
      { keys: [["Home"], ["End"]], what: "On the replay track, go to the first alert or to now" },
    ],
  },
  {
    title: "Flow editor",
    matches: (p) => p.startsWith("/flows/") && p !== "/flows/new",
    items: [{ keys: [["Esc"]], what: "Close the step panel" }],
  },
  {
    title: "Agent settings",
    matches: (p) => p.startsWith("/agents/"),
    items: [{ keys: [["←"], ["→"]], what: "Move between the settings tabs" }],
  },
  {
    title: "Guardian",
    matches: (p) => p === "/guardian",
    items: [{ keys: [["←"], ["→"]], what: "Move along the faults chart; Home and End jump" }],
  },
  {
    title: "Outcomes",
    matches: (p) => p === "/outcomes",
    items: [{ keys: [["←"], ["→"]], what: "Move along the weekly chart; Esc lets go" }],
  },
];

export function screenShortcuts(pathname: string): ShortcutGroup | null {
  return SCREEN_SHORTCUTS.find((s) => s.matches(pathname)) ?? null;
}

/** The overlay's groups for a screen: that screen's own first, then the global ones. */
export function shortcutGroups(pathname: string): ShortcutGroup[] {
  const here = screenShortcuts(pathname);
  return here
    ? [{ title: `On this screen: ${here.title}`, items: here.items }, ...GLOBAL_SHORTCUTS]
    : GLOBAL_SHORTCUTS;
}

/* Turning single-key shortcuts off ------------------------------------------------------- */

const SINGLE_KEYS = "zeaops.singleKeys";
const listeners = new Set<() => void>();
/** The choice for this visit when the browser keeps no storage. */
let remembered: boolean | null = null;

/** Whether single-character shortcuts are on (they are unless someone turned them off). */
export function singleKeysEnabled(): boolean {
  if (remembered !== null) return remembered;
  try {
    return window.localStorage.getItem(SINGLE_KEYS) !== "off";
  } catch {
    return true;
  }
}

export function setSingleKeys(on: boolean): void {
  try {
    if (on) window.localStorage.removeItem(SINGLE_KEYS);
    else window.localStorage.setItem(SINGLE_KEYS, "off");
    remembered = null;
  } catch {
    remembered = on;
  }
  listeners.forEach((l) => l());
}

export function subscribeSingleKeys(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Whether single-key shortcuts are on, following the switch wherever it is flipped. */
export function useSingleKeys(): boolean {
  return useSyncExternalStore(subscribeSingleKeys, singleKeysEnabled, () => true);
}

/** Whether a key press happened while typing, where character shortcuts must stay quiet. */
export function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  if (tag === "TEXTAREA" || tag === "SELECT") return true;
  if (tag !== "INPUT") return false;
  const type = (target as HTMLInputElement).type;
  // Checkboxes, radios, buttons and sliders take no text.
  return !["checkbox", "radio", "button", "submit", "reset", "range", "color", "file"].includes(
    type,
  );
}
